import type { RepoRef } from '../types/environment';

interface DefaultBranchResult {
  Repo: string;
  DefaultBranch: string;
}

const MAX_REPOS_PER_REQUEST = 50;

/**
 * Fetch the default branch for each repo from GitHub via the environment's
 * GitHub connection. Returns a map of "org/repo" -> default branch name.
 * Falls back to an empty map on error. Batches into chunks of 50 to
 * respect the API limit, with a 3s timeout per batch.
 */
export async function fetchDefaultBranches(
  environmentID: string,
  repos: RepoRef[],
): Promise<Record<string, string>> {
  const repoKeys = repos.map((r) => `${r.Org}/${r.Repo}`);
  if (!environmentID || repoKeys.length === 0) return {};
  const map: Record<string, string> = {};
  for (let i = 0; i < repoKeys.length; i += MAX_REPOS_PER_REQUEST) {
    const batch = repoKeys.slice(i, i + MAX_REPOS_PER_REQUEST);
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 3000);
      const resp = await fetch('/api/default-branches', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ EnvironmentID: environmentID, Repos: batch }),
        signal: controller.signal,
      });
      clearTimeout(timeout);
      if (!resp.ok) continue;
      const items: DefaultBranchResult[] = await resp.json();
      for (const item of items) {
        map[item.Repo] = item.DefaultBranch;
      }
    } catch {
      continue;
    }
  }
  return map;
}
